import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  WorkflowStageType,
  type QcTemplate,
  type WorkflowStageType as WorkflowStageTypeValue,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { isWholePositive, type DurationUnit } from '../schemas/duration';
import { CUSTOMER_STAGE_TYPES, type WorkflowFormInput } from '../schemas/workflow-form-schema';

import { DurationField } from './duration-field';
import { MemberMultiSelect } from './member-multi-select';
import { ParcelsEditor } from './parcels-editor';
import { StageEmailFields } from './stage-email-fields';
import { SubStepsEditor } from './sub-steps-editor';

export interface StageCardProps {
  storeId: string;
  index: number;
  total: number;
  expanded: boolean;
  onToggle: () => void;
  onMove: (to: number) => void;
  onRemove: () => void;
  qcTemplates: QcTemplate[];
  disabled?: boolean;
}

function useDurationText() {
  const { t } = useTranslation('workflows');
  return (value: string | undefined, unit: DurationUnit | undefined) =>
    value && isWholePositive(value) && unit
      ? t(`duration.${unit}`, { count: Number(value) })
      : t('stage.noLimit');
}

/** One stage of the template: a summary row with ordering, and the full editor when expanded. */
export function StageCard({
  storeId,
  index,
  total,
  expanded,
  onToggle,
  onMove,
  onRemove,
  qcTemplates,
  disabled = false,
}: StageCardProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<WorkflowFormInput>();
  const durationText = useDurationText();
  const prefix = `stages.${index}` as const;
  const stage = useWatch({ control: form.control, name: prefix });
  const hasError = Boolean(form.formState.errors.stages?.[index]);
  const open = expanded || hasError;
  const name = stage.name.trim() || t('stage.untitled');
  const customer = CUSTOMER_STAGE_TYPES.includes(stage.type);
  const bodyId = `stage-${index}-body`;
  const first = index === 0;

  const onTypeChange = (type: WorkflowStageTypeValue) => {
    if (type === 'shipping' && stage.parcels.length === 0) {
      form.setValue(
        `${prefix}.parcels`,
        [
          { key: 'ring', label: t('parcels.defaultRing'), carriesItems: true },
          { key: 'igi_certificate', label: t('parcels.defaultCertificate'), carriesItems: false },
        ],
        { shouldDirty: true },
      );
    }
  };

  return (
    <li
      aria-label={t('stage.label', { index: index + 1, name })}
      className={cn('bg-card rounded-lg border', hasError && 'border-destructive/60')}
    >
      <div className="flex flex-wrap items-center gap-3 p-3">
        <span className="bg-muted text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums">
          {index + 1}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{name}</span>
          <span className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
            {t(`type.${stage.type}`)}
            {' · '}
            {customer
              ? durationText(
                  stage.responseLimitValue || '24',
                  stage.responseLimitValue ? stage.responseLimitUnit : 'hours',
                )
              : durationText(stage.durationValue, stage.durationUnit)}
            {stage.type === 'task' && stage.subSteps.length > 0
              ? ` · ${stage.subSteps.map((sub) => sub.name || '…').join(', ')}`
              : null}
            {stage.optional ? <Badge variant="outline">{t('stage.optionalBadge')}</Badge> : null}
            {stage.hidden ? <Badge variant="outline">{t('stage.hiddenBadge')}</Badge> : null}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div
            className="flex items-center gap-1"
            data-tour={first ? 'workflows.editor.stage.reorder' : undefined}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('stage.moveUp', { name })}
              disabled={disabled || index === 0}
              onClick={() => onMove(index - 1)}
            >
              <ArrowUp aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('stage.moveDown', { name })}
              disabled={disabled || index === total - 1}
              onClick={() => onMove(index + 1)}
            >
              <ArrowDown aria-hidden />
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('stage.remove', { name })}
            disabled={disabled || total === 1}
            onClick={onRemove}
          >
            <Trash2 aria-hidden />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={open ? t('stage.collapse', { name }) : t('stage.expand', { name })}
            data-tour={first ? 'workflows.editor.stage.expand' : undefined}
            onClick={onToggle}
          >
            {open ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
          </Button>
        </div>
      </div>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-4 border-t p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name={`${prefix}.name`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stage.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={120}
                      placeholder={t('stage.namePlaceholder')}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${prefix}.type`}
              render={({ field }) => (
                <FormItem data-tour={first ? 'workflows.editor.stage.type' : undefined}>
                  <FormLabel>{t('stage.type')}</FormLabel>
                  <Select
                    value={field.value}
                    disabled={disabled}
                    onValueChange={(value) => {
                      field.onChange(value);
                      onTypeChange(value as WorkflowStageTypeValue);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(WorkflowStageType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`type.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t(`typeHint.${stage.type}`)}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${prefix}.customerName`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stage.customerName')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={120}
                      placeholder={t('stage.customerNamePlaceholder')}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormDescription>{t('stage.customerNameHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${prefix}.key`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stage.key')}</FormLabel>
                  <FormControl>
                    <Input {...field} className="font-mono" maxLength={60} disabled={disabled} />
                  </FormControl>
                  <FormDescription>{t('stage.keyHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name={`${prefix}.hidden`}
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-2">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  </FormControl>
                  <div className="flex flex-col gap-1">
                    <FormLabel className="font-normal">{t('stage.hidden')}</FormLabel>
                    <FormDescription>{t('stage.hiddenHint')}</FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${prefix}.optional`}
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-2">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  </FormControl>
                  <div className="flex flex-col gap-1">
                    <FormLabel className="font-normal">{t('stage.optional')}</FormLabel>
                    <FormDescription>{t('stage.optionalHint')}</FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
          {stage.type === 'customer_review' ? (
            <FormField
              control={form.control}
              name={`${prefix}.approvesDesign`}
              render={({ field }) => (
                <FormItem
                  className="flex flex-row items-start justify-between gap-4 rounded-md border p-3"
                  data-tour={first ? 'workflows.editor.stage.approves-design' : undefined}
                >
                  <div className="space-y-0.5">
                    <FormLabel>{t('stage.approvesDesign')}</FormLabel>
                    <FormDescription>{t('stage.approvesDesignHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ) : null}
          <div data-tour={first ? 'workflows.editor.stage.duration' : undefined}>
            {customer ? (
              <DurationField
                label={t('stage.responseLimit')}
                hint={t('stage.responseLimitHint')}
                valueName={`${prefix}.responseLimitValue`}
                unitName={`${prefix}.responseLimitUnit`}
                disabled={disabled}
              />
            ) : (
              <DurationField
                label={t('stage.duration')}
                hint={t('stage.durationHint')}
                valueName={`${prefix}.durationValue`}
                unitName={`${prefix}.durationUnit`}
                disabled={disabled}
              />
            )}
          </div>
          <FormField
            control={form.control}
            name={`${prefix}.alertUserIds`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stage.alerts')}</FormLabel>
                <FormControl>
                  <MemberMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    disabled={disabled}
                  />
                </FormControl>
                <FormDescription>{t('stage.alertsHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {stage.type === 'qc' ? (
            <FormField
              control={form.control}
              name={`${prefix}.qcTemplateId`}
              render={({ field }) => (
                <FormItem data-tour={first ? 'workflows.editor.stage.qc-checklist' : undefined}>
                  <FormLabel>{t('stage.qcTemplate')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-80">
                        <SelectValue placeholder={t('stage.qcTemplatePlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {qcTemplates.map((checklist) => (
                        <SelectItem key={checklist.id} value={checklist.id}>
                          {checklist.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {qcTemplates.length === 0 ? `${t('stage.qcTemplateEmpty')} ` : null}
                    <Link
                      to="/s/$storeId/settings/qc-checklists"
                      params={{ storeId }}
                      className="underline underline-offset-2"
                    >
                      {t('stage.qcTemplateManage')}
                    </Link>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          {stage.type === 'task' ? <SubStepsEditor stageIndex={index} disabled={disabled} /> : null}
          {stage.type === 'shipping' ? (
            <ParcelsEditor stageIndex={index} disabled={disabled} />
          ) : null}
          <StageEmailFields prefix={prefix} type={stage.type} disabled={disabled} tour={first} />
        </div>
      ) : null}
    </li>
  );
}
